import React, {useState, useEffect, useMemo} from 'react';
import {inject, observer} from 'mobx-react';
import ReactGA from "react-ga4";

import LLRouter from './common/router/router';
import {LLPreloader} from './components';
import {errorLogger, bootstrapI18n} from './common/utils';
import {TIMERS} from "./store/config";
import './common/utils/polyfills';
import 'react-toastify/dist/ReactToastify.min.css';
import './styles/scss/fonts.scss';
import './styles/scss/doruk-icons.scss';
import './App.scss';
import './styles/scss/sport_icon_font.scss';
import config from "./static/themes/config";


function App({store}) {
    const [isLoading, setLoading] = useState(true);

    useEffect(() => {
        const head = document.head;
        const link = document.createElement("link");
        const linkHref = () => {
            if (window.location.hostname.includes('dev.') || window.location.hostname.includes('devx.') || window.location.hostname.includes('localhost')) {
                return 'https://d3947mtbdsbloq.cloudfront.net/css-gen/sport_icon_font.css';
            } else if (window.location.hostname.includes('int.') || window.location.hostname.includes('intx.')) {
                return 'https://d3qp3l6hsapzl5.cloudfront.net/css-gen/sport_icon_font.css';
            } else {
                return 'https://d3nufycp49m9g1.cloudfront.net/css-gen/sport_icon_font.css';
            }
        };

        link.type = "text/css";
        link.rel = "stylesheet";
        link.href = linkHref();

        head.appendChild(link);

        return () => {
            head.removeChild(link);
        }

    }, []);

    useEffect(() => {
        if (['-dev'].find(el => window.location.href.includes(el))) {
            window.addEventListener("error", ({message, error, target: {location}}) => {
                errorLogger({errorObj: {message: message, error: error, location: location.href}});
            });
        }
        Promise.all([store.site.getConfig(), store.user.getSessionInfo()]).finally(
            async () => {
                // Dictionary from lang cookie (set during getSessionInfo);
                // language catalog from config.languageList (group-scoped).
                await bootstrapI18n(store.site.languageList);
                store.user.widgets.initializeInstruction({type: 'll'}).then(() => {
                    setLoading(false);
                    if (store.user.isLogged || store.site.isGuestCouponAllowed) {
                        store.user.coupons.getCouponsList();
                    }
                });
                if (!store.user.mobileUserAgent) {
                    document.body.classList.add('body--with-cursor');
                    if (document.body.clientWidth < 1367) {
                        document.body.classList.add('body--small-desktop');
                    }
                }

                if (window.location.href.includes('btag=')) {
                    const urlSearchPart = window.location.href;
                    const btag = urlSearchPart.substring(urlSearchPart.lastIndexOf('=') + 1, urlSearchPart.length);
                    store.user.setBTag(btag);
                }

                if (
                    !['localhost', '-dev', '-int', '-stg'].find(el => window.location.href.includes(el)) &&
                    config.withGoogleAnalytics
                ) {
                    ReactGA.initialize('G-JFL9YRQFW5');
                }
            }
        );
    }, []);

    useEffect(() => {
        if ((store.site.status.isGlobalError || !!store.betting.bettingErrors.global) && store.site.status.isActive && !isLoading) {
            window.location.reload();
        } else if (store.site.status.isActive && store.user.userBattleId) {
            clearTimeout(window._userBattleUpdateInterval);
            window._userBattleUpdateInterval = setTimeout(() => {
                store.user.setUserBattleUpdate(store.user.userBattleId);
            }, TIMERS.refreshBattleDataFetchTime);
        }
    }, [store.site.status.isActive]);

    useEffect(() => {
        document.addEventListener(
            'visibilitychange',
            store.site.status.checkPageActivity
        );
        return () =>
            document.removeEventListener(
                'visibilitychange',
                store.site.status.checkPageActivity
            );
    }, []);

    useEffect(() => {
        document.addEventListener(
            'closePopupEvt',
            store.modal.close
        )
    }, []);

    const renderApp = () => (isLoading ? null : <LLRouter/>);

    return (
        <>
            <LLPreloader fullPage visible={isLoading} height={50} width={50}/>
            {useMemo(() => renderApp(), [store.site.siteName, store.site.status.isGlobalError, isLoading])}
        </>
    );
}

export default inject('store')(observer(App));
